package com.arena_web.ui.components.overlay

import androidx.compose.runtime.Composable
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.utils.Constants
import com.arena_web.ui.utils.s
import com.arena_web.ui.utils.sWidth
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*

/**
 * Reusable desktop popup chrome — dimmed backdrop, centred `#09142d` card with
 * the `Button/border/gradient/primary` hairline (mask-composite overlay) and a
 * fade/scale entrance. The phone counterpart is [BottomSheet]; pairing a shared
 * content composable with one of these two lets a single body render as a
 * desktop popup or a mobile sheet.
 *
 * Caller controls visibility (`if (open) DesktopPopup { … }`); clicking the
 * backdrop calls [onDismiss], clicks inside the card don't propagate.
 */
@Composable
fun DesktopPopup(
    onDismiss: () -> Unit,
    widthPx: Number = 440,
    mobile: Boolean = false,
    content: @Composable () -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "fixed")
                property("inset", "0")
                property("z-index", "1100")
                property("background", "rgba(0, 0, 0, 0.6)")
            }
            .onClick { onDismiss() }
            .animation(
                ModalOverlayFadeInKeyframes.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .sWidth(widthPx)
                .styleModifier {
                    // On mobile, render at the 390-px baseline so `s()` inside
                    // the card (and `sWidth` here) tracks `--mobile-scale`.
                    if (mobile) property("--scale", "var(--mobile-scale)")
                    property("position", "relative")
                    property("border-radius", s(18))
                }
                .onClick { it.stopPropagation() }
                .animation(
                    ModalFadeInKeyframes.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
        ) {
            // Gradient border hairline.
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier {
                        property("position", "absolute")
                        property("inset", "0")
                        property("border", "0.3px solid transparent")
                        property("border-radius", s(18))
                        property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                        property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("-webkit-mask-composite", "xor")
                        property("mask-composite", "exclude")
                        property("pointer-events", "none")
                    }
            )

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("background", "#09142d")
                        property("border-radius", s(18))
                        property("padding", "${s(22)} ${s(24)} ${s(40)} ${s(24)}")
                        property("box-shadow", "0 20px 60px rgba(0,0,0,0.5)")
                    }
            ) {
                content()
            }
        }
    }
}
